import UnifiedEditor from "@/components/editor/UnifiedEditor";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { useEffect, useState } from "react";
import { BACKEND_URL } from "../config/env";
import { Helmet } from "react-helmet-async";
import { useSession } from "@/lib/auth-client";
import { Loader2 } from "lucide-react";

const BASE = BACKEND_URL || "";

function EditorRouteComponent() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isVerifyingOwnership, setIsVerifyingOwnership] = useState(false);
  const { data: session } = useSession();

  const helmet = (
    <Helmet>
      <title>editor</title>
      <meta name="robots" content="noindex,nofollow,noarchive,noimageindex" />
    </Helmet>
  );

  useEffect(() => {
    if (id === "new" || !session?.user?.id) return;

    let cancelled = false;
    const verifyOwnership = async () => {
      try {
        setIsVerifyingOwnership(true);
        setError(null);
        const res = await fetch(`${BASE}/api/bios/${id}`, {
          credentials: "include",
        });
        if (!res.ok) {
          if (res.status === 404) {
            window.history.back();
            return;
          }
          if (res.status === 401) {
            navigate({ to: "/sign-in", replace: true });
            return;
          }
          if (res.status === 403) {
            window.history.back();
            return;
          }
          throw new Error("failed to verify bio ownership");
        }
        const bioData = await res.json();
        if (!cancelled) {
          if (!bioData.isOwner) {
            window.history.back();
            return;
          }
          setIsVerifyingOwnership(false);
        }
      } catch (e) {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : "failed to verify ownership"
          );
          setIsVerifyingOwnership(false);
        }
      }
    };
    verifyOwnership();
    return () => {
      cancelled = true;
    };
  }, [id, session?.user?.id, navigate]);

  useEffect(() => {
    if (id !== "new") return;

    let cancelled = false;
    const createDraft = async () => {
      try {
        setError(null);
        const res = await fetch(`${BASE}/api/bios/draft`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({}),
          credentials: "include",
        });
        if (!res.ok) {
          if (res.status === 401) {
            navigate({ to: "/sign-in", replace: true });
            return;
          }
          const msg = await res.text();
          throw new Error(msg || "failed to create draft");
        }
        const data = await res.json();
        const newId = data?.id as string | undefined;
        if (newId && !cancelled) {
          navigate({
            to: "/editor/$id",
            params: { id: newId },
            search: {},
            replace: true,
          });
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : "failed to create draft");
        }
      }
    };
    createDraft();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  if (id === "new") {
    return (
      <ProtectedRoute>
        {helmet}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "60vh",
          }}
        >
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-accent" />
            {error ? (
              <div style={{ color: "red", marginTop: 8 }}>{error}</div>
            ) : null}
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  if (isVerifyingOwnership) {
    return (
      <ProtectedRoute>
        {helmet}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "60vh",
          }}
        >
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-6 w-6 animate-spin text-accent" />
            {error ? (
              <div style={{ color: "red", marginTop: 8 }}>{error}</div>
            ) : null}
          </div>
        </div>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      {helmet}
      <UnifiedEditor />
    </ProtectedRoute>
  );
}

export const Route = createFileRoute("/editor/$id")({
  component: EditorRouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      mode: typeof search.mode === "string" ? search.mode : undefined,
      seed: typeof search.seed === "string" ? search.seed : undefined,
    } as { mode?: string; seed?: string };
  },
});
